import type { QuizConfig } from '@/types/form.types'

interface RawQuizTemplateDefinition {
  id: string
  name: string
  description: string
  emoji: string
  defaultColors: {
    primaryColor: string
    backgroundColor: string
    correctColor: string
    incorrectColor: string
  }
  defaultSettings: {
    timeLimitSeconds: number
    passThreshold: number
    attemptLimit: number
    attemptWindow: QuizConfig['attemptWindow']
  }
  defaultRounds: {
    id: string
    label: string
    releaseType: 'immediate' | 'offset' | 'date'
    offsetDays?: number
    releaseDate?: string
    questions: {
      id: string
      text: string
      choices: { id: string; label: string }[]
      correctChoiceId?: string
      type?: 'single-select' | 'multi-select' | 'free-text' | 'rating' | 'boolean'
    }[]
  }[]
}

export interface QuizTemplateDefinition {
  id: string
  name: string
  description: string
  emoji: string
  defaultColors: RawQuizTemplateDefinition['defaultColors']
  defaultSettings: RawQuizTemplateDefinition['defaultSettings']
  defaultRounds: {
    id: string
    label: string
    stages: {
      id: string
      order: number
      type?: 'quiz' | 'survey'
      questions: RawQuizTemplateDefinition['defaultRounds'][number]['questions']
    }[]
  }[]
  defaultSlots: {
    id: string
    label: string
    roundId: string
    releaseType: 'immediate' | 'offset' | 'date'
    offsetDays?: number
    releaseDate?: string
  }[]
}

export const DEFAULT_QUIZ_TEMPLATE_ID = 'irl-quiz-general'
export const DEFAULT_SURVEY_TEMPLATE_ID = 'irl-survey-general'
export const QUIZ_TEMPLATE_GENERAL_KNOWLEDGE_ID = 'irl-quiz-general'
export const SURVEY_TEMPLATE_GENERAL_FEEDBACK_ID = 'irl-survey-general'

type RawQuizQuestion = RawQuizTemplateDefinition['defaultRounds'][number]['questions'][number]
type RawQuizRound = RawQuizTemplateDefinition['defaultRounds'][number]

function cloneRawQuestion(question: RawQuizQuestion): RawQuizQuestion {
  return {
    ...question,
    choices: question.choices.map((choice) => ({ ...choice })),
  }
}

function chunkQuestionsForWeeklyRelease(questions: RawQuizQuestion[], weeks = 4): RawQuizQuestion[][] {
  const total = questions.length
  const baseSize = Math.floor(total / weeks)
  const remainder = total % weeks
  const chunks: RawQuizQuestion[][] = []
  let index = 0

  for (let weekIndex = 0; weekIndex < weeks; weekIndex += 1) {
    const size = baseSize + (weekIndex < remainder ? 1 : 0)
    chunks.push(questions.slice(index, index + size).map(cloneRawQuestion))
    index += size
  }

  return chunks.filter(chunk => chunk.length > 0)
}

function buildWeeklyQuizRounds(roundPrefix: string, questions: RawQuizQuestion[]): RawQuizRound[] {
  return chunkQuestionsForWeeklyRelease(questions).map((chunk, index) => ({
    id: `${roundPrefix}-r${index + 1}`,
    label: `Week ${index + 1}`,
    releaseType: index === 0 ? 'immediate' : 'offset',
    offsetDays: index === 0 ? undefined : index * 7,
    questions: chunk.map((question, questionIndex) => ({
      ...question,
      id: `${roundPrefix}-r${index + 1}-${questionIndex + 1}`,
      choices: question.choices.map((choice) => ({ ...choice })),
    })),
  }))
}

function normalizeQuizTemplateRounds(template: RawQuizTemplateDefinition): RawQuizTemplateDefinition {
  if (template.defaultRounds.length !== 1) {
    return {
      ...template,
      defaultRounds: template.defaultRounds.map((round) => ({
        ...round,
        questions: round.questions.map(cloneRawQuestion),
      })),
    }
  }

  const [onlyRound] = template.defaultRounds
  if (onlyRound.questions.length <= 2) {
    return {
      ...template,
      defaultRounds: template.defaultRounds.map((round) => ({
        ...round,
        questions: round.questions.map(cloneRawQuestion),
      })),
    }
  }

  const roundPrefix = onlyRound.id.replace(/-r\d+$/, '')
  return {
    ...template,
    defaultRounds: buildWeeklyQuizRounds(roundPrefix, onlyRound.questions),
  }
}

const RAW_QUIZ_TEMPLATES: RawQuizTemplateDefinition[] = [
  {
    id: QUIZ_TEMPLATE_GENERAL_KNOWLEDGE_ID,
    name: 'General Knowledge',
    description: 'Classic trivia across topics. Edit questions to match your brand.',
    emoji: '🧠',
    defaultColors: {
      primaryColor: '#9B6DFF',
      backgroundColor: '#050614',
      correctColor: '#39FF14',
      incorrectColor: '#FF3131',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'gq-r1',
        label: 'Week 1',
        releaseType: 'immediate',
        questions: [
          {
            id: 'gq-r1-1',
            text: 'What is the capital of France?',
            choices: [
              { id: 'a', label: 'London' },
              { id: 'b', label: 'Berlin' },
              { id: 'c', label: 'Paris' },
            ],
            correctChoiceId: 'c',
          },
          {
            id: 'gq-r1-2',
            text: 'What is 2 + 2?',
            choices: [
              { id: 'a', label: '3' },
              { id: 'b', label: '4' },
              { id: 'c', label: '5' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
      {
        id: 'gq-r2',
        label: 'Week 2',
        releaseType: 'offset',
        offsetDays: 7,
        questions: [
          {
            id: 'gq-r2-1',
            text: 'How many sides does a triangle have?',
            choices: [
              { id: 'a', label: '3' },
              { id: 'b', label: '4' },
              { id: 'c', label: '5' },
            ],
            correctChoiceId: 'a',
          },
          {
            id: 'gq-r2-2',
            text: 'What colour is the sky on a clear day?',
            choices: [
              { id: 'a', label: 'Blue' },
              { id: 'b', label: 'Green' },
              { id: 'c', label: 'Red' },
            ],
            correctChoiceId: 'a',
          },
        ],
      },
      {
        id: 'gq-r3',
        label: 'Week 3',
        releaseType: 'offset',
        offsetDays: 14,
        questions: [
          {
            id: 'gq-r3-1',
            text: 'What year did World War 2 end?',
            choices: [
              { id: 'a', label: '1943' },
              { id: 'b', label: '1945' },
              { id: 'c', label: '1947' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'gq-r3-2',
            text: 'Which planet is known as the Red Planet?',
            choices: [
              { id: 'a', label: 'Venus' },
              { id: 'b', label: 'Jupiter' },
              { id: 'c', label: 'Mars' },
            ],
            correctChoiceId: 'c',
          },
        ],
      },
      {
        id: 'gq-r4',
        label: 'Week 4',
        releaseType: 'offset',
        offsetDays: 21,
        questions: [
          {
            id: 'gq-r4-1',
            text: 'How many continents are there on Earth?',
            choices: [
              { id: 'a', label: '5' },
              { id: 'b', label: '6' },
              { id: 'c', label: '7' },
            ],
            correctChoiceId: 'c',
          },
          {
            id: 'gq-r4-2',
            text: 'What is the largest ocean on Earth?',
            choices: [
              { id: 'a', label: 'Atlantic' },
              { id: 'b', label: 'Pacific' },
              { id: 'c', label: 'Indian' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-football',
    name: 'Football',
    description: 'Football trivia for the fans. Swap in your own team-specific questions.',
    emoji: '⚽',
    defaultColors: {
      primaryColor: '#00C853',
      backgroundColor: '#0A1628',
      correctColor: '#69FF47',
      incorrectColor: '#FF1744',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'fq-r1',
        label: 'Week 1',
        releaseType: 'immediate',
        questions: [
          {
            id: 'fq-r1-1',
            text: 'How many players are on a football team?',
            choices: [
              { id: 'a', label: '9' },
              { id: 'b', label: '11' },
              { id: 'c', label: '13' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'fq-r1-2',
            text: 'What shape is a football pitch?',
            choices: [
              { id: 'a', label: 'Circle' },
              { id: 'b', label: 'Rectangle' },
              { id: 'c', label: 'Triangle' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
      {
        id: 'fq-r2',
        label: 'Week 2',
        releaseType: 'offset',
        offsetDays: 7,
        questions: [
          {
            id: 'fq-r2-1',
            text: 'How long is a standard football match?',
            choices: [
              { id: 'a', label: '60 minutes' },
              { id: 'b', label: '90 minutes' },
              { id: 'c', label: '120 minutes' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'fq-r2-2',
            text: 'What does a red card mean in football?',
            choices: [
              { id: 'a', label: 'Warning' },
              { id: 'b', label: 'Substitution' },
              { id: 'c', label: 'Dismissal' },
            ],
            correctChoiceId: 'c',
          },
        ],
      },
      {
        id: 'fq-r3',
        label: 'Week 3',
        releaseType: 'offset',
        offsetDays: 14,
        questions: [
          {
            id: 'fq-r3-1',
            text: 'Which country hosts the English Premier League?',
            choices: [
              { id: 'a', label: 'Spain' },
              { id: 'b', label: 'England' },
              { id: 'c', label: 'Brazil' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'fq-r3-2',
            text: 'How many yellow cards earn a player a red card?',
            choices: [
              { id: 'a', label: '1' },
              { id: 'b', label: '2' },
              { id: 'c', label: '3' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
      {
        id: 'fq-r4',
        label: 'Week 4',
        releaseType: 'offset',
        offsetDays: 21,
        questions: [
          {
            id: 'fq-r4-1',
            text: 'What is the maximum number of substitutes allowed in a modern match?',
            choices: [
              { id: 'a', label: '3' },
              { id: 'b', label: '4' },
              { id: 'c', label: '5' },
            ],
            correctChoiceId: 'c',
          },
          {
            id: 'fq-r4-2',
            text: 'Which trophy is awarded to the best player at the FIFA World Cup?',
            choices: [
              { id: 'a', label: 'Golden Boot' },
              { id: 'b', label: 'Golden Ball' },
              { id: 'c', label: 'Golden Glove' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-ai-business',
    name: 'AI in Business',
    description: 'Test your team\'s AI literacy. How well do you know AI in the workplace?',
    emoji: '🤖',
    defaultColors: {
      primaryColor: '#6C63FF',
      backgroundColor: '#080B1A',
      correctColor: '#00FFB3',
      incorrectColor: '#FF4C6A',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'aiq-r1',
        label: 'Week 1',
        releaseType: 'immediate',
        questions: [
          {
            id: 'aiq-r1-1',
            text: 'What is the most practical first step when introducing AI into a business?',
            choices: [
              { id: 'a', label: 'Replace all staff with AI immediately' },
              { id: 'b', label: 'Identify one repetitive task AI could handle' },
              { id: 'c', label: 'Build a custom AI model from scratch' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'aiq-r1-2',
            text: 'What does a large language model (LLM) like ChatGPT primarily do?',
            choices: [
              { id: 'a', label: 'Browse the internet in real time' },
              { id: 'b', label: 'Generate text based on patterns learned from training data' },
              { id: 'c', label: 'Store and retrieve company documents' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
      {
        id: 'aiq-r2',
        label: 'Week 2',
        releaseType: 'offset',
        offsetDays: 7,
        questions: [
          {
            id: 'aiq-r2-1',
            text: 'Which of these is a real risk when using AI-generated content in business?',
            choices: [
              { id: 'a', label: 'The AI will take over your computer' },
              { id: 'b', label: 'Confidential data entered into public tools may be used for training' },
              { id: 'c', label: 'AI always produces the same output so it gets boring' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'aiq-r2-2',
            text: 'What is "prompt engineering" in the context of AI tools?',
            choices: [
              { id: 'a', label: 'Writing software code for AI systems' },
              { id: 'b', label: 'Crafting clear instructions to get better outputs from AI' },
              { id: 'c', label: 'Maintaining the physical servers that run AI' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
      {
        id: 'aiq-r3',
        label: 'Week 3',
        releaseType: 'offset',
        offsetDays: 14,
        questions: [
          {
            id: 'aiq-r3-1',
            text: 'Which business function has seen the most productivity gains from AI tools so far?',
            choices: [
              { id: 'a', label: 'Physical manufacturing and construction' },
              { id: 'b', label: 'Content creation, summarisation, and written communication' },
              { id: 'c', label: 'In-person customer service' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'aiq-r3-2',
            text: 'What does "hallucination" mean when describing AI output?',
            choices: [
              { id: 'a', label: 'The AI generates animated visuals' },
              { id: 'b', label: 'The AI shuts down unexpectedly' },
              { id: 'c', label: 'The AI confidently produces incorrect or fabricated information' },
            ],
            correctChoiceId: 'c',
          },
        ],
      },
      {
        id: 'aiq-r4',
        label: 'Week 4',
        releaseType: 'offset',
        offsetDays: 21,
        questions: [
          {
            id: 'aiq-r4-1',
            text: 'Which of these best describes a retrieval-augmented generation (RAG) system?',
            choices: [
              { id: 'a', label: 'An AI that only works offline' },
              { id: 'b', label: 'An AI that searches a knowledge base before generating a response' },
              { id: 'c', label: 'An AI that generates images from text' },
            ],
            correctChoiceId: 'b',
          },
          {
            id: 'aiq-r4-2',
            text: 'What is the main benefit of using AI for data analysis in business?',
            choices: [
              { id: 'a', label: 'It replaces the need for any human judgment' },
              { id: 'b', label: 'It can identify patterns in large datasets faster than humans' },
              { id: 'c', label: 'It always gives 100% accurate results' },
            ],
            correctChoiceId: 'b',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-music-legends',
    name: 'Music Legends',
    description: 'Iconic artists, chart-topping hits, and pop culture music moments.',
    emoji: '🎵',
    defaultColors: {
      primaryColor: '#FF5E7E',
      backgroundColor: '#140A16',
      correctColor: '#7CFFB2',
      incorrectColor: '#FF4D6D',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'ml-r1',
        label: 'Music Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'ml-r1-1',
            text: 'Which artist is known as the "King of Pop"?',
            choices: [
              { id: 'a', label: 'Prince' },
              { id: 'b', label: 'Lionel Richie' },
              { id: 'c', label: 'Stevie Wonder' },
              { id: 'd', label: 'Michael Jackson' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-2',
            text: 'Which 80s icon performed "Like a Virgin" at the first-ever MTV VMAs?',
            choices: [
              { id: 'a', label: 'Cyndi Lauper' },
              { id: 'b', label: 'Debbie Harry' },
              { id: 'c', label: 'Pat Benatar' },
              { id: 'd', label: 'Madonna' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-3',
            text: 'Prince famously hailed from which American city?',
            choices: [
              { id: 'a', label: 'Detroit' },
              { id: 'b', label: 'Chicago' },
              { id: 'c', label: 'Nashville' },
              { id: 'd', label: 'Minneapolis' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-4',
            text: 'Which band was fronted by Freddie Mercury?',
            choices: [
              { id: 'a', label: 'The Who' },
              { id: 'b', label: 'Led Zeppelin' },
              { id: 'c', label: 'Pink Floyd' },
              { id: 'd', label: 'Queen' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-5',
            text: 'Which modern superstar has a massive fan base known as "Swifties"?',
            choices: [
              { id: 'a', label: 'Katy Perry' },
              { id: 'b', label: 'Ariana Grande' },
              { id: 'c', label: 'Selena Gomez' },
              { id: 'd', label: 'Taylor Swift' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-6',
            text: 'Whitney Houston\'s "I Will Always Love You" was originally by whom?',
            choices: [
              { id: 'a', label: 'Reba McEntire' },
              { id: 'b', label: 'Shania Twain' },
              { id: 'c', label: 'Linda Ronstadt' },
              { id: 'd', label: 'Dolly Parton' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-7',
            text: 'Which artist\'s 2020 hit "Blinding Lights" broke records for time spent in the Top 10?',
            choices: [
              { id: 'a', label: 'Drake' },
              { id: 'b', label: 'Bruno Mars' },
              { id: 'c', label: 'Post Malone' },
              { id: 'd', label: 'The Weeknd' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-8',
            text: 'Who was the lead singer of the 80s band Culture Club?',
            choices: [
              { id: 'a', label: 'George Michael' },
              { id: 'b', label: 'Morrissey' },
              { id: 'c', label: 'Adam Ant' },
              { id: 'd', label: 'Boy George' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-9',
            text: 'As of 2024, which female artist holds the record for most Grammy wins?',
            choices: [
              { id: 'a', label: 'Adele' },
              { id: 'b', label: 'Lady Gaga' },
              { id: 'c', label: 'Rihanna' },
              { id: 'd', label: 'Beyonce' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ml-r1-10',
            text: 'Annie Lennox was one half of which iconic 80s duo?',
            choices: [
              { id: 'a', label: 'Yazoo' },
              { id: 'b', label: 'Wham!' },
              { id: 'c', label: 'Pet Shop Boys' },
              { id: 'd', label: 'Eurythmics' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-sci-fi-fantasy',
    name: 'Sci-Fi & Fantasy',
    description: 'Blockbuster universes, legendary heroes, and fan-favorite lore.',
    emoji: '🚀',
    defaultColors: {
      primaryColor: '#7A5CFF',
      backgroundColor: '#090D1F',
      correctColor: '#62F2C1',
      incorrectColor: '#FF5C8A',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'sf-r1',
        label: 'Sci-Fi & Fantasy Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'sf-r1-1',
            text: 'What is Han Solo\'s Wookiee co-pilot\'s name?',
            choices: [
              { id: 'a', label: 'Admiral Ackbar' },
              { id: 'b', label: 'Lando Calrissian' },
              { id: 'c', label: 'Greedo' },
              { id: 'd', label: 'Chewbacca' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-2',
            text: 'Who created the "One Ring" in the fires of Mount Doom?',
            choices: [
              { id: 'a', label: 'Saruman' },
              { id: 'b', label: 'Morgoth' },
              { id: 'c', label: 'Gandalf' },
              { id: 'd', label: 'Sauron' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-3',
            text: 'What fictional metal is bonded to Wolverine\'s skeleton?',
            choices: [
              { id: 'a', label: 'Vibranium' },
              { id: 'b', label: 'Kryptonite' },
              { id: 'c', label: 'Mithril' },
              { id: 'd', label: 'Adamantium' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-4',
            text: 'In Harry Potter, what is the wizarding prison guarded by Dementors?',
            choices: [
              { id: 'a', label: 'Nurmengard' },
              { id: 'b', label: 'Gringotts' },
              { id: 'c', label: 'Godric\'s Hollow' },
              { id: 'd', label: 'Azkaban' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-5',
            text: 'What is the main desert planet in the Dune universe?',
            choices: [
              { id: 'a', label: 'Tatooine' },
              { id: 'b', label: 'Geonosis' },
              { id: 'c', label: 'Caladan' },
              { id: 'd', label: 'Arrakis' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-6',
            text: 'In The Matrix, which pill does Neo take to see the truth?',
            choices: [
              { id: 'a', label: 'The Blue Pill' },
              { id: 'b', label: 'The Green Pill' },
              { id: 'c', label: 'The Yellow Pill' },
              { id: 'd', label: 'The Red Pill' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-7',
            text: 'What is the home planet of the Transformers?',
            choices: [
              { id: 'a', label: 'Coruscant' },
              { id: 'b', label: 'Krypton' },
              { id: 'c', label: 'Skaro' },
              { id: 'd', label: 'Cybertron' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-8',
            text: 'What is the name of Daenerys Targaryen\'s largest dragon?',
            choices: [
              { id: 'a', label: 'Viserion' },
              { id: 'b', label: 'Rhaegal' },
              { id: 'c', label: 'Smaug' },
              { id: 'd', label: 'Drogon' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-9',
            text: 'Which superhero is also known as the "Man of Tomorrow"?',
            choices: [
              { id: 'a', label: 'Batman' },
              { id: 'b', label: 'Iron Man' },
              { id: 'c', label: 'Captain America' },
              { id: 'd', label: 'Superman' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'sf-r1-10',
            text: 'Which Star Trek captain famously commanded the USS Enterprise (NCC-1701)?',
            choices: [
              { id: 'a', label: 'Jean-Luc Picard' },
              { id: 'b', label: 'Benjamin Sisko' },
              { id: 'c', label: 'Kathryn Janeway' },
              { id: 'd', label: 'James T. Kirk' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-history-mysteries',
    name: 'History\'s Mysteries',
    description: 'Ancient empires, unexplained events, and major turning points in history.',
    emoji: '🏛️',
    defaultColors: {
      primaryColor: '#D4A24C',
      backgroundColor: '#16120D',
      correctColor: '#9EF01A',
      incorrectColor: '#FF6B6B',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'hm-r1',
        label: 'History Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'hm-r1-1',
            text: 'Who was the last active pharaoh of Ptolemaic Egypt?',
            choices: [
              { id: 'a', label: 'Nefertiti' },
              { id: 'b', label: 'Hatshepsut' },
              { id: 'c', label: 'Twosret' },
              { id: 'd', label: 'Cleopatra' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-2',
            text: 'The "Mary Celeste" was found drifting in 1872. What was it?',
            choices: [
              { id: 'a', label: 'A hot air balloon' },
              { id: 'b', label: 'A steam train' },
              { id: 'c', label: 'A stagecoach' },
              { id: 'd', label: 'A merchant ship' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-3',
            text: 'The Black Death plague devastated Europe during which century?',
            choices: [
              { id: 'a', label: '12th Century' },
              { id: 'b', label: '16th Century' },
              { id: 'c', label: '18th Century' },
              { id: 'd', label: '14th Century' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-4',
            text: 'Who was the main author of the U.S. Declaration of Independence?',
            choices: [
              { id: 'a', label: 'Benjamin Franklin' },
              { id: 'b', label: 'John Adams' },
              { id: 'c', label: 'George Washington' },
              { id: 'd', label: 'Thomas Jefferson' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-5',
            text: 'The Taj Mahal was built by which emperor as a tomb for his wife?',
            choices: [
              { id: 'a', label: 'Akbar the Great' },
              { id: 'b', label: 'Aurangzeb' },
              { id: 'c', label: 'Babur' },
              { id: 'd', label: 'Shah Jahan' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-6',
            text: 'What was the code name for the WWII atomic bomb project?',
            choices: [
              { id: 'a', label: 'Apollo Program' },
              { id: 'b', label: 'Overlord Project' },
              { id: 'c', label: 'Enigma Project' },
              { id: 'd', label: 'Manhattan Project' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-7',
            text: 'Stonehenge is located in which modern-day country?',
            choices: [
              { id: 'a', label: 'Scotland' },
              { id: 'b', label: 'Ireland' },
              { id: 'c', label: 'Wales' },
              { id: 'd', label: 'England' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-8',
            text: 'Who led the first expedition to reach the South Pole in 1911?',
            choices: [
              { id: 'a', label: 'Robert Falcon Scott' },
              { id: 'b', label: 'Ernest Shackleton' },
              { id: 'c', label: 'Richard Byrd' },
              { id: 'd', label: 'Roald Amundsen' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-9',
            text: 'In which year did the Berlin Wall finally fall?',
            choices: [
              { id: 'a', label: '1985' },
              { id: 'b', label: '1991' },
              { id: 'c', label: '1979' },
              { id: 'd', label: '1989' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'hm-r1-10',
            text: 'Joan of Arc was a heroine from which country?',
            choices: [
              { id: 'a', label: 'England' },
              { id: 'b', label: 'Spain' },
              { id: 'c', label: 'Italy' },
              { id: 'd', label: 'France' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-animal-kingdom',
    name: 'Animal Kingdom',
    description: 'Wildlife facts, record holders, and strange animal trivia.',
    emoji: '🦁',
    defaultColors: {
      primaryColor: '#F59E0B',
      backgroundColor: '#102012',
      correctColor: '#86EFAC',
      incorrectColor: '#FB7185',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'ak-r1',
        label: 'Animal Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'ak-r1-1',
            text: 'What is the only mammal naturally capable of true flight?',
            choices: [
              { id: 'a', label: 'Flying Squirrel' },
              { id: 'b', label: 'Sugar Glider' },
              { id: 'c', label: 'Pterodactyl' },
              { id: 'd', label: 'The Bat' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-2',
            text: 'What do you call a group of flamingos?',
            choices: [
              { id: 'a', label: 'A murder' },
              { id: 'b', label: 'A pride' },
              { id: 'c', label: 'A parliament' },
              { id: 'd', label: 'A flamboyance' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-3',
            text: 'How many hearts does an octopus have?',
            choices: [
              { id: 'a', label: 'One' },
              { id: 'b', label: 'Two' },
              { id: 'c', label: 'Eight' },
              { id: 'd', label: 'Three' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-4',
            text: 'Which land animal has the largest eyes?',
            choices: [
              { id: 'a', label: 'The Elephant' },
              { id: 'b', label: 'The Giraffe' },
              { id: 'c', label: 'The Giant Squid' },
              { id: 'd', label: 'The Ostrich' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-5',
            text: 'Which is the fastest land snake in the world?',
            choices: [
              { id: 'a', label: 'King Cobra' },
              { id: 'b', label: 'Rattlesnake' },
              { id: 'c', label: 'Copperhead' },
              { id: 'd', label: 'Black Mamba' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-6',
            text: 'What is a rhinoceros\' horn primarily made of?',
            choices: [
              { id: 'a', label: 'Bone' },
              { id: 'b', label: 'Ivory' },
              { id: 'c', label: 'Cartilage' },
              { id: 'd', label: 'Keratin' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-7',
            text: 'Which animal is credited with the world\'s strongest bite force?',
            choices: [
              { id: 'a', label: 'Great White Shark' },
              { id: 'b', label: 'Grizzly Bear' },
              { id: 'c', label: 'Hippopotamus' },
              { id: 'd', label: 'Nile Crocodile' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-8',
            text: 'What is the specific name for a baby kangaroo?',
            choices: [
              { id: 'a', label: 'A calf' },
              { id: 'b', label: 'A cub' },
              { id: 'c', label: 'A kid' },
              { id: 'd', label: 'A joey' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-9',
            text: 'Which bird is the only one that can fly backward?',
            choices: [
              { id: 'a', label: 'Swift' },
              { id: 'b', label: 'Swallow' },
              { id: 'c', label: 'Woodpecker' },
              { id: 'd', label: 'Hummingbird' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'ak-r1-10',
            text: 'What is the largest species of "big cat" in the world?',
            choices: [
              { id: 'a', label: 'Lion' },
              { id: 'b', label: 'Jaguar' },
              { id: 'c', label: 'Leopard' },
              { id: 'd', label: 'Tiger' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-mythology',
    name: 'Mythology',
    description: 'Gods, monsters, and legendary tales from ancient myth.',
    emoji: '⚡',
    defaultColors: {
      primaryColor: '#A78BFA',
      backgroundColor: '#130B1F',
      correctColor: '#34D399',
      incorrectColor: '#F87171',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'my-r1',
        label: 'Mythology Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'my-r1-1',
            text: 'Who is the King of the Gods in Greek mythology?',
            choices: [
              { id: 'a', label: 'Poseidon' },
              { id: 'b', label: 'Hades' },
              { id: 'c', label: 'Apollo' },
              { id: 'd', label: 'Zeus' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-2',
            text: 'What is the name of Thor\'s hammer in Norse mythology?',
            choices: [
              { id: 'a', label: 'Gungnir' },
              { id: 'b', label: 'Stormbreaker' },
              { id: 'c', label: 'Aegis' },
              { id: 'd', label: 'Mjolnir' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-3',
            text: 'Which Egyptian god has the head of a jackal?',
            choices: [
              { id: 'a', label: 'Horus' },
              { id: 'b', label: 'Set' },
              { id: 'c', label: 'Sobek' },
              { id: 'd', label: 'Anubis' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-4',
            text: 'Who is the Greek goddess of wisdom?',
            choices: [
              { id: 'a', label: 'Aphrodite' },
              { id: 'b', label: 'Hera' },
              { id: 'c', label: 'Artemis' },
              { id: 'd', label: 'Athena' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-5',
            text: 'In Norse myth, what is the name of the "End of the World"?',
            choices: [
              { id: 'a', label: 'Valhalla' },
              { id: 'b', label: 'Asgard' },
              { id: 'c', label: 'Jotunheim' },
              { id: 'd', label: 'Ragnarok' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-6',
            text: 'Which Greek hero was vulnerable only in his heel?',
            choices: [
              { id: 'a', label: 'Hercules' },
              { id: 'b', label: 'Perseus' },
              { id: 'c', label: 'Odysseus' },
              { id: 'd', label: 'Achilles' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-7',
            text: 'What creature was half-man and half-bull?',
            choices: [
              { id: 'a', label: 'Centaur' },
              { id: 'b', label: 'Satyr' },
              { id: 'c', label: 'Chimera' },
              { id: 'd', label: 'Minotaur' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-8',
            text: 'Who was the Egyptian Sun God?',
            choices: [
              { id: 'a', label: 'Osiris' },
              { id: 'b', label: 'Thoth' },
              { id: 'c', label: 'Ptah' },
              { id: 'd', label: 'Ra' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-9',
            text: 'What is the name of the three-headed dog guarding the Underworld?',
            choices: [
              { id: 'a', label: 'Hydra' },
              { id: 'b', label: 'Chimera' },
              { id: 'c', label: 'Fenrir' },
              { id: 'd', label: 'Cerberus' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'my-r1-10',
            text: 'Who is the Norse God of Mischief?',
            choices: [
              { id: 'a', label: 'Balder' },
              { id: 'b', label: 'Heimdall' },
              { id: 'c', label: 'Freyr' },
              { id: 'd', label: 'Loki' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-viral-trends',
    name: 'Viral Trends & Memes',
    description: 'Internet slang, meme culture, and social moments that blew up online.',
    emoji: '🔥',
    defaultColors: {
      primaryColor: '#F43F5E',
      backgroundColor: '#1A1020',
      correctColor: '#2DD4BF',
      incorrectColor: '#FB7185',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'vt-r1',
        label: 'Viral Trends Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'vt-r1-1',
            text: 'Which 2023 viral meme series featured a singing toilet with a human head?',
            choices: [
              { id: 'a', label: 'Baby Shark' },
              { id: 'b', label: 'Gummy Bear' },
              { id: 'c', label: 'Coffin Dance' },
              { id: 'd', label: 'Skibidi Toilet' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-2',
            text: 'What does the slang term "Rizz" stand for?',
            choices: [
              { id: 'a', label: 'Risk' },
              { id: 'b', label: 'Resilience' },
              { id: 'c', label: 'Rhythm' },
              { id: 'd', label: 'Charisma' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-3',
            text: 'What does the slang term "Cap" mean?',
            choices: [
              { id: 'a', label: 'A hat' },
              { id: 'b', label: 'A winner' },
              { id: 'c', label: 'A secret' },
              { id: 'd', label: 'A lie' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-4',
            text: 'The Dress sparked a global debate in 2015. Which color combinations did people argue over?',
            choices: [
              { id: 'a', label: 'Red/Green and Blue/Yellow' },
              { id: 'b', label: 'Purple/Gold and Pink/White' },
              { id: 'c', label: 'Black/Grey and Silver/Blue' },
              { id: 'd', label: 'Blue/Black and White/Gold' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-5',
            text: 'The term "Sus" was popularized largely by which video game in 2020?',
            choices: [
              { id: 'a', label: 'Roblox' },
              { id: 'b', label: 'Fortnite' },
              { id: 'c', label: 'Fall Guys' },
              { id: 'd', label: 'Among Us' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-6',
            text: 'What does "IYKYK" stand for?',
            choices: [
              { id: 'a', label: 'I Yield, Keep Your Knowledge' },
              { id: 'b', label: 'I\'m Young, Keep Your Kindness' },
              { id: 'c', label: 'It\'s Yours, Keep Your Keys' },
              { id: 'd', label: 'If You Know, You Know' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-7',
            text: 'Which stuffed animal brand became a massive collectible trend for Gen Z?',
            choices: [
              { id: 'a', label: 'Beanie Babies' },
              { id: 'b', label: 'Furby' },
              { id: 'c', label: 'Webkinz' },
              { id: 'd', label: 'Squishmallows' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-8',
            text: 'In 2024, which water bottle brand became a status symbol among teens?',
            choices: [
              { id: 'a', label: 'Hydro Flask' },
              { id: 'b', label: 'Yeti' },
              { id: 'c', label: 'CamelBak' },
              { id: 'd', label: 'Stanley' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'vt-r1-9',
            text: 'What is the name of the NPC streamer who went viral for saying "Ice cream so good"?',
            choices: [
              { id: 'a', label: 'Bella Poarch' },
              { id: 'b', label: 'Addison Rae' },
              { id: 'c', label: 'Charli D\'Amelio' },
              { id: 'd', label: 'PinkyDoll' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-modern-gaming',
    name: 'Modern Gaming',
    description: 'Fortnite, Roblox, Minecraft, and other online gaming culture staples.',
    emoji: '🎮',
    defaultColors: {
      primaryColor: '#22C55E',
      backgroundColor: '#0A1322',
      correctColor: '#6EE7B7',
      incorrectColor: '#F43F5E',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'mg-r1',
        label: 'Gaming Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'mg-r1-1',
            text: 'What is the currency used in the game Roblox?',
            choices: [
              { id: 'a', label: 'V-Bucks' },
              { id: 'b', label: 'Minecoins' },
              { id: 'c', label: 'Gems' },
              { id: 'd', label: 'Robux' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-2',
            text: 'In Fortnite, what is the name of the blue bus that drops players onto the island?',
            choices: [
              { id: 'a', label: 'Party Bus' },
              { id: 'b', label: 'Sky Bus' },
              { id: 'c', label: 'Victory Bus' },
              { id: 'd', label: 'Battle Bus' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-3',
            text: 'Which Minecraft mob is green, silent, and explodes when it gets close to you?',
            choices: [
              { id: 'a', label: 'Zombie' },
              { id: 'b', label: 'Enderman' },
              { id: 'c', label: 'Skeleton' },
              { id: 'd', label: 'Creeper' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-4',
            text: 'In Among Us, what is the role of the player trying to sabotage the crew?',
            choices: [
              { id: 'a', label: 'Traitor' },
              { id: 'b', label: 'Spy' },
              { id: 'c', label: 'Villain' },
              { id: 'd', label: 'Impostor' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-5',
            text: 'Which YouTuber is known for his massive giveaway videos and Squid Game recreation?',
            choices: [
              { id: 'a', label: 'Dream' },
              { id: 'b', label: 'PewDiePie' },
              { id: 'c', label: 'Markiplier' },
              { id: 'd', label: 'MrBeast' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-6',
            text: 'What is the name of the default skin in Minecraft?',
            choices: [
              { id: 'a', label: 'Alex' },
              { id: 'b', label: 'Herobrine' },
              { id: 'c', label: 'Notch' },
              { id: 'd', label: 'Steve' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-7',
            text: 'In Fortnite, what resource is gathered by hitting blue rocks?',
            choices: [
              { id: 'a', label: 'Brick' },
              { id: 'b', label: 'Wood' },
              { id: 'c', label: 'Obsidian' },
              { id: 'd', label: 'Metal' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-8',
            text: 'Which game allows users to create their own experiences and games within its platform?',
            choices: [
              { id: 'a', label: 'Minecraft' },
              { id: 'b', label: 'The Sims' },
              { id: 'c', label: 'League of Legends' },
              { id: 'd', label: 'Roblox' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-9',
            text: 'The Floss dance was popularized globally by which game?',
            choices: [
              { id: 'a', label: 'Just Dance' },
              { id: 'b', label: 'Overwatch' },
              { id: 'c', label: 'FIFA' },
              { id: 'd', label: 'Fortnite' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mg-r1-10',
            text: 'What is the name of the main antagonist in the Five Nights at Freddy\'s series?',
            choices: [
              { id: 'a', label: 'Freddy Fazbear' },
              { id: 'b', label: 'Mike Schmidt' },
              { id: 'c', label: 'Phone Guy' },
              { id: 'd', label: 'William Afton' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-streaming-influencers',
    name: 'Streaming & Influencers',
    description: 'Creators, platforms, and internet personalities driving online culture.',
    emoji: '📺',
    defaultColors: {
      primaryColor: '#38BDF8',
      backgroundColor: '#0E1628',
      correctColor: '#5EEAD4',
      incorrectColor: '#FB7185',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'si-r1',
        label: 'Streaming Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'si-r1-1',
            text: 'Which platform is the world\'s leading service for live-streaming video games?',
            choices: [
              { id: 'a', label: 'Kick' },
              { id: 'b', label: 'YouTube Gaming' },
              { id: 'c', label: 'Facebook Gaming' },
              { id: 'd', label: 'Twitch' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-2',
            text: 'Kai Cenat is famous for his subathons on which platform?',
            choices: [
              { id: 'a', label: 'TikTok' },
              { id: 'b', label: 'Instagram' },
              { id: 'c', label: 'Twitter/X' },
              { id: 'd', label: 'Twitch' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-3',
            text: 'Which influencer had the most-followed TikTok account in the world as of early 2024?',
            choices: [
              { id: 'a', label: 'Charli D\'Amelio' },
              { id: 'b', label: 'Bella Poarch' },
              { id: 'c', label: 'Zach King' },
              { id: 'd', label: 'Khabane Lame' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-4',
            text: 'What is the term for a creator who uses a digital avatar instead of showing their real face?',
            choices: [
              { id: 'a', label: 'AI-Tuber' },
              { id: 'b', label: 'Digi-Streamer' },
              { id: 'c', label: 'Mask-Vlogger' },
              { id: 'd', label: 'VTuber' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-5',
            text: 'Which creator hosted the boxing event called Creator Clash?',
            choices: [
              { id: 'a', label: 'Logan Paul' },
              { id: 'b', label: 'KSI' },
              { id: 'c', label: 'Speed' },
              { id: 'd', label: 'iDubbbz' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-6',
            text: 'The Sidemen are a famous group of YouTubers from which country?',
            choices: [
              { id: 'a', label: 'USA' },
              { id: 'b', label: 'Australia' },
              { id: 'c', label: 'Canada' },
              { id: 'd', label: 'United Kingdom' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-7',
            text: 'Which streamer is known for energetic barking and obsession with Cristiano Ronaldo?',
            choices: [
              { id: 'a', label: 'Adin Ross' },
              { id: 'b', label: 'xQc' },
              { id: 'c', label: 'Ludwig' },
              { id: 'd', label: 'IShowSpeed' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-8',
            text: 'Emma Chamberlain is most famous for her start on which platform?',
            choices: [
              { id: 'a', label: 'Musical.ly' },
              { id: 'b', label: 'Vine' },
              { id: 'c', label: 'Pinterest' },
              { id: 'd', label: 'YouTube' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-9',
            text: 'Which creator founded the Prime hydration drink alongside KSI?',
            choices: [
              { id: 'a', label: 'Jake Paul' },
              { id: 'b', label: 'MrBeast' },
              { id: 'c', label: 'David Dobrik' },
              { id: 'd', label: 'Logan Paul' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'si-r1-10',
            text: 'What is the term for a short, looping video on Instagram?',
            choices: [
              { id: 'a', label: 'Snap' },
              { id: 'b', label: 'Story' },
              { id: 'c', label: 'Short' },
              { id: 'd', label: 'Reel' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-modern-pop-hip-hop',
    name: 'Modern Pop & Hip-Hop',
    description: 'Post-2015 chart hits, breakout stars, and pop culture music trivia.',
    emoji: '🎤',
    defaultColors: {
      primaryColor: '#F472B6',
      backgroundColor: '#1B1024',
      correctColor: '#67E8F9',
      incorrectColor: '#FB7185',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'mph-r1',
        label: 'Pop & Hip-Hop Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'mph-r1-1',
            text: 'Which artist released the album "Sour" and stars in "High School Musical: The Musical: The Series"?',
            choices: [
              { id: 'a', label: 'Sabrina Carpenter' },
              { id: 'b', label: 'Billie Eilish' },
              { id: 'c', label: 'Tate McRae' },
              { id: 'd', label: 'Olivia Rodrigo' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-2',
            text: 'What is the name of Billie Eilish\'s brother and main producer?',
            choices: [
              { id: 'a', label: 'Phineas' },
              { id: 'b', label: 'Felix' },
              { id: 'c', label: 'Franklin' },
              { id: 'd', label: 'Finneas' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-3',
            text: 'Which artist is famous for the hit "Old Town Road"?',
            choices: [
              { id: 'a', label: 'Post Malone' },
              { id: 'b', label: 'Jack Harlow' },
              { id: 'c', label: 'DaBaby' },
              { id: 'd', label: 'Lil Nas X' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-4',
            text: 'The Barbie movie soundtrack featured the hit "Dance the Night" by which artist?',
            choices: [
              { id: 'a', label: 'Ice Spice' },
              { id: 'b', label: 'Nicki Minaj' },
              { id: 'c', label: 'Taylor Swift' },
              { id: 'd', label: 'Dua Lipa' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-5',
            text: 'Which K-Pop group became the first to headline Coachella?',
            choices: [
              { id: 'a', label: 'BTS' },
              { id: 'b', label: 'NewJeans' },
              { id: 'c', label: 'TWICE' },
              { id: 'd', label: 'BLACKPINK' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-6',
            text: '"Espresso" and "Please Please Please" are 2024 hits by which singer?',
            choices: [
              { id: 'a', label: 'Ariana Grande' },
              { id: 'b', label: 'Selena Gomez' },
              { id: 'c', label: 'Camila Cabello' },
              { id: 'd', label: 'Sabrina Carpenter' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-7',
            text: 'Which rapper is known for the Astroworld festival and the Cactus Jack brand?',
            choices: [
              { id: 'a', label: 'Drake' },
              { id: 'b', label: 'Kendrick Lamar' },
              { id: 'c', label: 'Future' },
              { id: 'd', label: 'Travis Scott' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-8',
            text: 'SZA\'s 2022 hit album is titled after which distress signal?',
            choices: [
              { id: 'a', label: 'MAYDAY' },
              { id: 'b', label: 'HELP' },
              { id: 'c', label: '911' },
              { id: 'd', label: 'SOS' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-9',
            text: 'Which artist is known for his signature heart logo and the album "Certified Lover Boy"?',
            choices: [
              { id: 'a', label: 'Kanye West' },
              { id: 'b', label: 'The Weeknd' },
              { id: 'c', label: 'J. Cole' },
              { id: 'd', label: 'Drake' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'mph-r1-10',
            text: 'Doja Cat\'s viral breakout song "Mooo!" features her dressed as what?',
            choices: [
              { id: 'a', label: 'A cat' },
              { id: 'b', label: 'A dog' },
              { id: 'c', label: 'An alien' },
              { id: 'd', label: 'A cow' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
  {
    id: 'irl-quiz-tv-movies-streaming',
    name: 'TV & Movies',
    description: 'Streaming-era hits, big-screen favorites, and binge-worthy trivia.',
    emoji: '🎬',
    defaultColors: {
      primaryColor: '#60A5FA',
      backgroundColor: '#0F172A',
      correctColor: '#4ADE80',
      incorrectColor: '#F87171',
    },
    defaultSettings: {
      timeLimitSeconds: 30,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'tm-r1',
        label: 'TV & Movies Quiz',
        releaseType: 'immediate',
        questions: [
          {
            id: 'tm-r1-1',
            text: 'In Stranger Things, what is the name of the parallel dimension?',
            choices: [
              { id: 'a', label: 'The Dark World' },
              { id: 'b', label: 'The Nether' },
              { id: 'c', label: 'The Void' },
              { id: 'd', label: 'The Upside Down' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-2',
            text: 'Which Netflix show features a deadly competition involving children\'s games?',
            choices: [
              { id: 'a', label: 'Alice in Borderland' },
              { id: 'b', label: 'Hunger Games' },
              { id: 'c', label: 'The Platform' },
              { id: 'd', label: 'Squid Game' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-3',
            text: 'Who plays Wednesday Addams in the 2022 Netflix series Wednesday?',
            choices: [
              { id: 'a', label: 'Maya Hawke' },
              { id: 'b', label: 'Sadie Sink' },
              { id: 'c', label: 'Millie Bobby Brown' },
              { id: 'd', label: 'Jenna Ortega' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-4',
            text: 'In Euphoria, which character is the narrator played by Zendaya?',
            choices: [
              { id: 'a', label: 'Jules' },
              { id: 'b', label: 'Maddy' },
              { id: 'c', label: 'Cassie' },
              { id: 'd', label: 'Rue' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-5',
            text: 'Which movie won the Oscar for Best Picture in 2023 and features a multiverse?',
            choices: [
              { id: 'a', label: 'Spider-Man: Into the Spider-Verse' },
              { id: 'b', label: 'Doctor Strange' },
              { id: 'c', label: 'Oppenheimer' },
              { id: 'd', label: 'Everything Everywhere All At Once' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-6',
            text: 'What is the name of the fictional town where Riverdale takes place?',
            choices: [
              { id: 'a', label: 'Greendale' },
              { id: 'b', label: 'Hawkins' },
              { id: 'c', label: 'Mystic Falls' },
              { id: 'd', label: 'Riverdale' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-7',
            text: 'In the MCU, what is the real name of Spider-Man?',
            choices: [
              { id: 'a', label: 'Miles Morales' },
              { id: 'b', label: 'Gwen Stacy' },
              { id: 'c', label: 'Eddie Brock' },
              { id: 'd', label: 'Peter Parker' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-8',
            text: 'Which animated series features characters named Rick and Morty?',
            choices: [
              { id: 'a', label: 'Solar Opposites' },
              { id: 'b', label: 'Family Guy' },
              { id: 'c', label: 'Adventure Time' },
              { id: 'd', label: 'Rick and Morty' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-9',
            text: 'In The Last of Us (HBO), what is the name of the fungus that causes the outbreak?',
            choices: [
              { id: 'a', label: 'T-Virus' },
              { id: 'b', label: 'Mycelium' },
              { id: 'c', label: 'Spores' },
              { id: 'd', label: 'Cordyceps' },
            ],
            correctChoiceId: 'd',
          },
          {
            id: 'tm-r1-10',
            text: 'Which show follows a group of high schoolers at Moordale Secondary School?',
            choices: [
              { id: 'a', label: 'Heartstopper' },
              { id: 'b', label: 'Elite' },
              { id: 'c', label: 'Gossip Girl' },
              { id: 'd', label: 'Sex Education' },
            ],
            correctChoiceId: 'd',
          },
        ],
      },
    ],
  },
]

const RAW_SURVEY_TEMPLATES: RawQuizTemplateDefinition[] = [
  {
    id: SURVEY_TEMPLATE_GENERAL_FEEDBACK_ID,
    name: 'General Feedback',
    description: 'A no-pressure survey starter for feedback, sentiment, and quick customer responses.',
    emoji: '📝',
    defaultColors: {
      primaryColor: '#4CC9F0',
      backgroundColor: '#101828',
      correctColor: '#4CC9F0',
      incorrectColor: '#F97316',
    },
    defaultSettings: {
      timeLimitSeconds: 600,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'gs-r1',
        label: 'Survey',
        releaseType: 'immediate',
        questions: [
          {
            id: 'gs-r1-1',
            text: 'How would you rate your overall experience today?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'gs-r1-2',
            text: 'What stood out most about the experience?',
            type: 'free-text',
            choices: [],
          },
        ],
      },
    ],
  },
  {
    id: 'irl-survey-customer-satisfaction',
    name: 'Customer Satisfaction',
    description: 'A CSAT-style survey starter for service quality, value, and likelihood to recommend.',
    emoji: '⭐',
    defaultColors: {
      primaryColor: '#14B8A6',
      backgroundColor: '#0F172A',
      correctColor: '#14B8A6',
      incorrectColor: '#F97316',
    },
    defaultSettings: {
      timeLimitSeconds: 600,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'csat-r1',
        label: 'Customer Survey',
        releaseType: 'immediate',
        questions: [
          {
            id: 'csat-r1-1',
            text: 'How satisfied were you with your experience today?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'csat-r1-2',
            text: 'How likely are you to recommend us to a friend or colleague?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'csat-r1-3',
            text: 'What could we improve next time?',
            type: 'free-text',
            choices: [],
          },
        ],
      },
    ],
  },
  {
    id: 'irl-survey-event-feedback',
    name: 'Event Feedback',
    description: 'A fast post-event template for speaker feedback, venue score, and follow-up interest.',
    emoji: '🎤',
    defaultColors: {
      primaryColor: '#F59E0B',
      backgroundColor: '#1F2937',
      correctColor: '#F59E0B',
      incorrectColor: '#EF4444',
    },
    defaultSettings: {
      timeLimitSeconds: 600,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'event-r1',
        label: 'Event Survey',
        releaseType: 'immediate',
        questions: [
          {
            id: 'event-r1-1',
            text: 'How would you rate the event overall?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'event-r1-2',
            text: 'Which part of the event was most valuable?',
            type: 'free-text',
            choices: [],
          },
          {
            id: 'event-r1-3',
            text: 'Would you attend another event from us?',
            type: 'boolean',
            choices: [],
          },
        ],
      },
    ],
  },
  {
    id: 'irl-survey-product-feedback',
    name: 'Product Feedback',
    description: 'A simple product-feedback starter for usefulness, ease of use, and improvement ideas.',
    emoji: '📦',
    defaultColors: {
      primaryColor: '#8B5CF6',
      backgroundColor: '#111827',
      correctColor: '#8B5CF6',
      incorrectColor: '#F97316',
    },
    defaultSettings: {
      timeLimitSeconds: 600,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'product-r1',
        label: 'Product Survey',
        releaseType: 'immediate',
        questions: [
          {
            id: 'product-r1-1',
            text: 'How easy was the product to use today?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'product-r1-2',
            text: 'Which feature delivered the most value for you?',
            type: 'free-text',
            choices: [],
          },
          {
            id: 'product-r1-3',
            text: 'What should we improve next?',
            type: 'free-text',
            choices: [],
          },
        ],
      },
    ],
  },
  {
    id: 'irl-survey-brand-pulse',
    name: 'Brand Pulse',
    description: 'A lightweight brand-sentiment starter for recall, trust, and future intent.',
    emoji: '💬',
    defaultColors: {
      primaryColor: '#EC4899',
      backgroundColor: '#0F172A',
      correctColor: '#EC4899',
      incorrectColor: '#F97316',
    },
    defaultSettings: {
      timeLimitSeconds: 600,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'week',
    },
    defaultRounds: [
      {
        id: 'brand-r1',
        label: 'Brand Survey',
        releaseType: 'immediate',
        questions: [
          {
            id: 'brand-r1-1',
            text: 'How positively do you feel about the brand right now?',
            type: 'rating',
            choices: [],
          },
          {
            id: 'brand-r1-2',
            text: 'What words come to mind when you think about the brand?',
            type: 'free-text',
            choices: [],
          },
          {
            id: 'brand-r1-3',
            text: 'How likely are you to engage with the brand again?',
            type: 'rating',
            choices: [],
          },
        ],
      },
    ],
  },
]

const NORMALIZED_QUIZ_TEMPLATES = RAW_QUIZ_TEMPLATES.map(normalizeQuizTemplateRounds)

function buildDefaultTemplate(mode: 'quiz' | 'survey'): QuizTemplateDefinition {
  const isQuiz = mode === 'quiz'
  return {
    id: 'default',
    name: 'Default',
    description: 'Empty experience — start from a blank slate.',
    emoji: '✨',
    defaultColors: {
      primaryColor: '#9B6DFF',
      backgroundColor: '#050614',
      correctColor: '#39FF14',
      incorrectColor: '#FF3131',
    },
    defaultSettings: {
      timeLimitSeconds: 60,
      passThreshold: 1,
      attemptLimit: 1,
      attemptWindow: 'day',
    },
    defaultRounds: [
      {
        id: 'round-1',
        label: 'Round 1',
        stages: [
          {
            id: 'round-1-stage-1',
            order: 1,
            type: isQuiz ? 'quiz' : 'survey',
            questions: [],
          },
        ],
      },
    ],
    defaultSlots: [
      {
        id: 'slot-round-1',
        label: 'Round 1',
        roundId: 'round-1',
        releaseType: 'immediate',
        offsetDays: undefined,
        releaseDate: undefined,
      },
    ],
  }
}

const DEFAULT_QUIZ_TEMPLATE: QuizTemplateDefinition = buildDefaultTemplate('quiz')
const DEFAULT_SURVEY_TEMPLATE: QuizTemplateDefinition = buildDefaultTemplate('survey')

export const QUIZ_TEMPLATES: QuizTemplateDefinition[] = [DEFAULT_QUIZ_TEMPLATE, ...NORMALIZED_QUIZ_TEMPLATES.map((template) => ({
  ...template,
  defaultRounds: template.defaultRounds.map((round) => ({
    id: round.id,
    label: round.label,
    stages: [
      {
        id: `${round.id}-stage-1`,
        order: 1,
        type: 'quiz' as const,
        questions: round.questions.map((question) => ({
          ...question,
          choices: question.choices.map((choice) => ({ ...choice })),
        })),
      },
    ],
  })),
  defaultSlots: template.defaultRounds.map((round) => ({
    id: `slot-${round.id}`,
    label: round.label,
    roundId: round.id,
    releaseType: round.releaseType,
    offsetDays: round.offsetDays,
    releaseDate: round.releaseDate,
  })),
}))]

export const SURVEY_TEMPLATES: QuizTemplateDefinition[] = [DEFAULT_SURVEY_TEMPLATE, ...RAW_SURVEY_TEMPLATES.map((template) => ({
  ...template,
  defaultRounds: template.defaultRounds.map((round) => ({
    id: round.id,
    label: round.label,
    stages: [
      {
        id: `${round.id}-stage-1`,
        order: 1,
        type: 'survey' as const,
        questions: round.questions.map((question) => ({
          ...question,
          choices: question.choices.map((choice) => ({ ...choice })),
        })),
      },
    ],
  })),
  defaultSlots: template.defaultRounds.map((round) => ({
    id: `slot-${round.id}`,
    label: round.label,
    roundId: round.id,
    releaseType: round.releaseType,
    offsetDays: round.offsetDays,
    releaseDate: round.releaseDate,
  })),
}))]

/**
 * Synthetic "Default" template family. Selecting this on a fresh campaign
 * applies the canonical default rules (60s time limit, threshold 1, attempts
 * 1, daily window, 00:00 rollover, leaderboard off) and an empty rounds list
 * — no pre-populated questions, no theme labels. Distinct from
 * `DEFAULT_QUIZ_TEMPLATE_ID` (which is the default *theme*); this is the
 * "no theme" choice.
 */
export const DEFAULT_TEMPLATE_FAMILY_ID = 'default'

/**
 * Rule defaults applied when the creator picks the synthetic 'default'
 * template family. Kept small + literal so tests can pin them.
 */
export interface DefaultTemplateRules {
    timeLimitSeconds: 60
    passThreshold: 1
    attemptLimit: 1
    attemptWindow: 'day'
    attemptWindowRolloverTime: '00:00'
    attemptWindowRolloverWeekday: 0
    leaderboardEnabled: false
}

export function buildDefaultTemplateRules(): DefaultTemplateRules {
    return {
        timeLimitSeconds: 60,
        passThreshold: 1,
        attemptLimit: 1,
        attemptWindow: 'day',
        attemptWindowRolloverTime: '00:00',
        attemptWindowRolloverWeekday: 0,
        leaderboardEnabled: false,
    }
}

export const getDefaultTemplateIdForExperienceMode = (
  mode?: QuizConfig['experienceMode'],
): string => (mode === 'survey' ? DEFAULT_SURVEY_TEMPLATE_ID : DEFAULT_QUIZ_TEMPLATE_ID)

export const getTemplatesForExperienceMode = (
  mode?: QuizConfig['experienceMode'],
): QuizTemplateDefinition[] => (mode === 'survey' ? SURVEY_TEMPLATES : QUIZ_TEMPLATES)

/**
 * Build a fully populated QuizConfig from a template definition.
 * Used to pre-populate the store with a ready-to-submit quiz config.
 */
export function buildQuizConfigFromTemplate(
  templateId: string = DEFAULT_QUIZ_TEMPLATE_ID,
  mode: QuizConfig['experienceMode'] = 'quiz',
): QuizConfig | null {
  const templates = mode === 'survey' ? SURVEY_TEMPLATES : QUIZ_TEMPLATES
  const tpl = templates.find(t => t.id === templateId)
  if (!tpl) return null

  return {
    experienceMode: mode,
    quizTemplateId: tpl.id,
    timeLimitSeconds: tpl.defaultSettings.timeLimitSeconds,
    passThreshold: tpl.defaultSettings.passThreshold,
    attemptLimit: tpl.defaultSettings.attemptLimit,
    attemptWindow: tpl.defaultSettings.attemptWindow,
    rounds: tpl.defaultRounds.map(r => ({
      ...r,
      stages: r.stages.map(stage => ({
        ...stage,
        type: (stage as any).type || mode,
        questions: stage.questions.map(q => ({
          ...q,
          choices: q.choices.map(c => ({ ...c })),
        })),
      })),
    })),
    slots: tpl.defaultSlots.map(slot => ({ ...slot })),
    noActiveSlotAction: null,
    leaderboard: {
      enabled: false,
      eventName: 'quiz.completed.score',
      displayMode: 'top-10-plus-user',
      title: 'Top Players',
      emptyStateText: 'Be the first to post a score.',
      showAfterCompletionOnly: true,
    },
    primaryColor: tpl.defaultColors.primaryColor,
    backgroundColor: tpl.defaultColors.backgroundColor,
    correctColor: tpl.defaultColors.correctColor,
    incorrectColor: tpl.defaultColors.incorrectColor,
  }
}
